/**
 * "First time signing in?" — the one-time invite CODE channel of an ESS
 * invite, for the WagePro mobile app (and the web, which is the same build).
 *
 * The invitation email carries both a set-password link and a 10-character
 * one-time code (`XXXXX-XXXXX`). An employee on their phone, who cannot
 * easily click a link into the installed app, opens the app, taps "First
 * time signing in?" on the login screen and lands here:
 *
 *   1. **Code step** — email + code → `essInviteSignup.lookupByCode`
 *      (throttled server-side and sharing the login lockout; a wrong pair
 *      gets the same generic error as a wrong password).
 *   2. **Set-password step** — the forced `InviteSetPasswordForm`, in
 *      create-account or existing-password mode, submitting
 *      `essInviteSignup.acceptWithCode`. No session exists until then.
 *
 * The email and code live in React state only — never storage, never the
 * URL — and vanish with the component.
 *
 * Rendered by `main.tsx`'s pathname gate on `INVITE_CODE_PATH`, before
 * `<AuthProvider>` mounts, like the login/signup/invite-accept screens.
 */
import { useState } from 'react';
import { useTranslation } from '@mukil/i18n';
import { Button, FormField, GlassCard, Input } from '@jumpstone/ui-react';
import { getPublicAccountTrpcClient } from '../../lib/auth/public_account_trpc.js';
import {
  classifyAuthError,
  type AuthErrorDisplay,
} from '../../lib/auth/auth_error_display.js';
import {
  hasInviteCodeFieldErrors,
  normalizeInviteCode,
  validateInviteCodeForm,
  type InviteCodeFieldErrors,
} from '../../lib/auth/invite_accept_logic.js';
import type { InviteAcceptSubmitDeps } from '../../lib/auth/invite_accept_submit.js';
import { LOGIN_PATH } from '../../lib/auth/wagepro_login_logic.js';
import { WageProAuthLayout } from '../../components/layout/wagepro_auth_layout.js';
import { AuthAltActionLink } from './auth_alt_action_link.js';
import { AuthFormError } from './auth_form_error.js';
import { InviteSetPasswordForm } from './invite_set_password_form.js';

export interface InviteCodeLookupResult {
  invited_email: string;
  company_name: string;
  account_exists: boolean;
  expires_at: string;
}

export type InviteCodeLookup = (input: {
  email: string;
  code: string;
}) => Promise<InviteCodeLookupResult>;

function lookupViaPublicClient(input: {
  email: string;
  code: string;
}): Promise<InviteCodeLookupResult> {
  return getPublicAccountTrpcClient().essInviteSignup.lookupByCode.mutate(
    input,
  );
}

type Step =
  | { kind: 'enter_code' }
  | { kind: 'checking' }
  | { kind: 'error'; error: AuthErrorDisplay }
  | {
      kind: 'set_password';
      email: string;
      code: string;
      result: InviteCodeLookupResult;
    };

export function InviteCodeScreen({
  lookup = lookupViaPublicClient,
  submitDeps,
  onAccepted,
}: {
  /** Test seam for `essInviteSignup.lookupByCode`. */
  lookup?: InviteCodeLookup;
  /** Test seam forwarded to the set-password form. */
  submitDeps?: Partial<InviteAcceptSubmitDeps>;
  onAccepted?: () => void;
}): React.JSX.Element {
  const { t } = useTranslation('wp');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [fieldErrors, setFieldErrors] = useState<InviteCodeFieldErrors>({});
  const [step, setStep] = useState<Step>({ kind: 'enter_code' });

  async function handleLookup(): Promise<void> {
    const errors = validateInviteCodeForm({ email, code });
    setFieldErrors(errors);
    if (hasInviteCodeFieldErrors(errors)) return;

    const trimmedEmail = email.trim();
    const normalizedCode = normalizeInviteCode(code);
    setStep({ kind: 'checking' });
    try {
      const result = await lookup({
        email: trimmedEmail,
        code: normalizedCode,
      });
      setStep({
        kind: 'set_password',
        email: trimmedEmail,
        code: normalizedCode,
        result,
      });
    } catch (err) {
      setStep({ kind: 'error', error: classifyAuthError(err) });
    }
  }

  const checking = step.kind === 'checking';

  return (
    <WageProAuthLayout>
      <GlassCard variant="strong" className="rounded-2xl p-8 w-full">
        {step.kind === 'set_password' ? (
          <InviteSetPasswordForm
            credential={{ kind: 'code', email: step.email, code: step.code }}
            invitedEmail={step.result.invited_email}
            companyName={step.result.company_name}
            accountExists={step.result.account_exists}
            onAccepted={onAccepted}
            deps={submitDeps}
          />
        ) : (
          <>
            <div className="mb-6">
              <h1 className="text-xl font-semibold text-[color:var(--ink)]">
                {t('invite_code.title')}
              </h1>
              <p className="mt-1 text-sm text-[color:var(--ink-muted)]">
                {t('invite_code.dek')}
              </p>
            </div>

            <form
              onSubmit={(event) => {
                event.preventDefault();
                void handleLookup();
              }}
              className="flex flex-col gap-4"
            >
              <FormField
                label={t('invite_code.email_label')}
                htmlFor="invite-code-email"
                error={fieldErrors.email && t('invite_code.email_error')}
              >
                <Input
                  id="invite-code-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  disabled={checking}
                />
              </FormField>

              <FormField
                label={t('invite_code.code_label')}
                htmlFor="invite-code-value"
                helpText={t('invite_code.code_hint')}
                error={fieldErrors.code && t('invite_code.code_error')}
              >
                <Input
                  id="invite-code-value"
                  className="mono"
                  autoComplete="one-time-code"
                  autoCapitalize="characters"
                  autoCorrect="off"
                  spellCheck={false}
                  inputMode="text"
                  maxLength={32}
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                  disabled={checking}
                />
              </FormField>

              {step.kind === 'error' && <AuthFormError error={step.error} />}

              <Button
                type="submit"
                variant="primary"
                loading={checking}
                className="mt-2 w-full justify-center"
              >
                {checking
                  ? t('invite_code.checking')
                  : t('invite_code.continue')}
              </Button>
            </form>

            <AuthAltActionLink
              href={LOGIN_PATH}
              label={t('invite_code.back_to_sign_in')}
            />
          </>
        )}
      </GlassCard>
    </WageProAuthLayout>
  );
}
